import { StaticColors } from 'common/colors/constants';
// UseCases
export const Internal = 'Internal';
export const Private = 'Private';
export const Public = 'Public';

// OnboardingSteps
export const Completed = 'Completed';
export const CreateBoard = 'CreateBoard';
export const CustomizePage = 'CustomizePage';
export const FiveBehalfVotes = 'FiveBehalfVotes';
export const FiveContributors = 'FiveContributors';
export const InstallCanny = 'InstallCanny';
export const InstalledIntegration = 'InstalledIntegration';
export const InviteTeammates = 'InviteTeammates';
export const PrivateBoard = 'PrivateBoard';
export const ThreePosts = 'ThreePosts';
export const ThreeTeammates = 'ThreeTeammates';
export const TwoTeammates = 'TwoTeammates';

const OnboardingChecks = {
  [CreateBoard]: ({ boardCount }) => boardCount > 0,
  [CustomizePage]: ({ faviconURL, logoURL, tintColor }) =>
    faviconURL !== null || logoURL !== null || tintColor !== StaticColors.canny,
  [FiveBehalfVotes]: ({ contributorCount }) => contributorCount > 4,
  [FiveContributors]: ({ contributorCount }) => contributorCount > 4,
  [InstallCanny]: ({ stats }) => stats.hasUsedIdentify || stats.hasUsedSSO,
  [InstalledIntegration]: ({ integrationCount }) => integrationCount > 0,
  [InviteTeammates]: ({ inviteCount, members }) => inviteCount > 0 || members.length > 1,
  [PrivateBoard]: ({ hasUnlistedBoard }) => hasUnlistedBoard,
  [ThreePosts]: ({ postCount }) => postCount > 2,
  [ThreeTeammates]: ({ members }) => members.length > 2,
  [TwoTeammates]: ({ members }) => members.length > 1,
};

export const OnboardingLabels = {
  [CreateBoard]: 'Create a board',
  [CustomizePage]: 'Customize your page',
  [FiveContributors]: 'Collect feedback',
  [InstalledIntegration]: 'Connect an integration',
  [ThreePosts]: 'Create 3 posts',
  [TwoTeammates]: 'Invite your team',
};

export const OnboardingDescriptions = {
  [CreateBoard]: 'Start organizing your feedback',
  [CustomizePage]: 'Make Canny match your brand',
  [FiveContributors]: 'Track customer feedback in Canny',
  [InstalledIntegration]: 'Get Canny working with your existing workflow',
  [ThreePosts]: 'Add some ideas you’ve heard recently',
  [TwoTeammates]: 'Have another teammate join your Canny team',
};

export const OnboardingLinks = {
  [CreateBoard]: '/admin/create-board',
  [CustomizePage]: '/admin/settings/company',
  [FiveContributors]: 'https://canny.io/blog/make-your-user-feedback-tool-more-accessible/',
  [InstalledIntegration]: '/admin/settings/integrations',
  [TwoTeammates]: '/admin/settings/team',
};

const getUseCase = ({ useCase }) => {
  const { customerFeedback, publicFeedback, teammateFeedback } = useCase;
  if (teammateFeedback) {
    return Internal;
  } else if (customerFeedback && !publicFeedback) {
    return Private;
  }
  return Public;
};

export const getOnboardingSteps = (company) => {
  const steps = [CreateBoard, CustomizePage, InviteTeammates];

  const useCase = getUseCase(company);
  if (useCase === Internal) {
    steps.push(PrivateBoard);
  } else if (useCase === Private) {
    steps.push(PrivateBoard);
  } else if (useCase === Public) {
    steps.push(ThreePosts);
  }

  if (useCase === Internal) {
    steps.push(ThreePosts);
  } else if (useCase === Private) {
    steps.push(ThreePosts);
  } else if (useCase === Public) {
    steps.push(InstallCanny);
  }

  if (useCase === Internal) {
    steps.push(ThreeTeammates);
  } else if (useCase === Private) {
    steps.push(FiveBehalfVotes);
  } else if (useCase === Public) {
    steps.push(FiveContributors);
  }

  return steps.map((step, index) => ({
    index,
    step,
    useCase,
  }));
};

const getOnboardingStep = (company) => {
  const useCase = getUseCase(company);

  if (company.contributorCount > 25) {
    return {
      step: Completed,
      useCase,
    };
  }
  const onboardingSteps = getOnboardingSteps(company);
  const index = onboardingSteps.findIndex(({ step }) => {
    const check = OnboardingChecks[step];
    const isStepComplete = check(company);
    return !isStepComplete;
  });
  const step = onboardingSteps[index] ? onboardingSteps[index].step : Completed;
  return {
    index: index < 0 ? onboardingSteps.length : index,
    step,
    useCase,
  };
};

export const getOnboardingChecklist = (company) => {
  const checklist = [
    CreateBoard,
    CustomizePage,
    ThreePosts,
    TwoTeammates,
    InstalledIntegration,
    FiveContributors,
  ];

  return checklist.map((item) => {
    const check = OnboardingChecks[item];
    const completed = check(company);
    return {
      name: item,
      completed,
    };
  });
};

export const getOnboardingTasks = (company) => {
  const checklist = [
    CreateBoard,
    CustomizePage,
    TwoTeammates,
    InstalledIntegration,
    FiveContributors,
  ];

  return checklist.map((item) => {
    const check = OnboardingChecks[item];
    const completed = check(company);
    return {
      completed,
      name: item,
    };
  });
};

export default getOnboardingStep;
